import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  List,
  SxProps,
  Typography,
  Zoom,
  useTheme,
} from '@mui/material';
import { CreditCardModel, useGetCards } from '@/logic/services/credit-cards';
import { FormattedMessage, useIntl } from 'react-intl';
import React, { FC, useCallback, useEffect } from 'react';
import { useAuth, useDialog } from '@/logic/state';

import AddIcon from '@mui/icons-material/Add';
import { CreditCardItem } from './credit-card-item';
import { CreditCardsListEmptyState } from './credit-cards-list-empty-state';
import { CreditCardsListLoader } from './credit-cards-list-loader';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { PROFILE_CARDS_TRANSITION_TIMEOUT } from '@/constants/profile';

import { pxToRem } from '@/theme/typography';
import shallow from 'zustand/shallow';
import { useQueryClient } from '@tanstack/react-query';
import ImageBoxFuture from '../shared/image-box-future';

const creditCardIcon = '/icons/profile/credit-card/creditCard.svg';

interface AccordionCardProps {
  accordion: SxProps;
  accordionBoxIcon: SxProps;
  accordionTitle: SxProps;
}
export const CreditCardAccordion: FC<AccordionCardProps> = ({
  accordion,
  accordionBoxIcon,
  accordionTitle,
}) => {
  const theme = useTheme();
  const intl = useIntl();
  const setDialog = useDialog(s => s.setDialog, shallow);
  const queryClient = useQueryClient();
  const user = useAuth(s => s.user, shallow);

  const refetch = useCallback(() => {
    queryClient.invalidateQueries([
      {
        method: 'get',
        url: 'cards',
        params: {
          limit: 50,
          page: 1,
        },
      },
    ]);
  }, [queryClient]);

  const { data: creditCards, isFetching: isCreditCardsFetching } = useGetCards({
    params: {
      limit: 50,
      page: 1,
    },
  });

  useEffect(() => {
    user && refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const openAddCreditCardForm = useCallback(() => {
    setDialog({
      wrapper: true,
      isAddCreditCardForm: true,
      addCreditCardFormPayload: {
        isBookingFlow: false,
        title: 'my_credit_cards.add_card_form.title',
      },
    });
  }, [setDialog]);

  return (
    <Zoom in timeout={PROFILE_CARDS_TRANSITION_TIMEOUT}>
      <Accordion
        sx={{
          maxWidth: '910px',
          mb: {
            tablet: `${pxToRem(44)} !important`,
            mobile: '32px !important',
          },
        }}
      >
        <AccordionSummary
          id='my-saved-cards'
          expandIcon={
            <ExpandMoreIcon
              sx={{
                color: theme.palette.primary.main,
                width: { tablet: pxToRem(24), mobile: '24px' },
                height: { tablet: pxToRem(24), mobile: '24px' },
                alignItems: 'center',
              }}
            />
          }
          sx={{
            ...accordion,
          }}
        >
          <Box
            component='div'
            sx={{
              ...accordionBoxIcon,
              backgroundColor: theme.palette.primary.main,
            }}
          >
            <ImageBoxFuture
              img={creditCardIcon}
              title={intl.formatMessage({
                id: 'alt.credit_card_icon',
              })}
              objectFit='contain'
              sx={{
                width: { tablet: pxToRem(33), mobile: '40px' },
                height: { tablet: pxToRem(33), mobile: '24px' },
              }}
            />
          </Box>
          <Typography component='h2' sx={accordionTitle}>
            <FormattedMessage id='my_credit_cards' />
          </Typography>
        </AccordionSummary>
        <AccordionDetails
          sx={{
            mt: { tablet: pxToRem(40), mobile: '24px' },
            position: 'relative',
          }}
        >
          <Box
            component='div'
            sx={{
              background: theme.palette.common.white,
              boxShadow: theme.customShadows.cardShadow,
              borderRadius: '16px',
              p: { tablet: pxToRem(32), mobile: '22px' },
              pl: { tablet: pxToRem(20), mobile: '17px' },
            }}
          >
            {isCreditCardsFetching ? (
              <CreditCardsListLoader />
            ) : creditCards?.data?.length > 0 ? (
              <List sx={{ p: 0 }}>
                {creditCards?.data?.map((card: CreditCardModel) => (
                  <CreditCardItem
                    key={card.id}
                    cardDetails={card}
                    refetchCards={() => refetch()}
                  />
                ))}
              </List>
            ) : (
              <CreditCardsListEmptyState message='my_credit_cards.no_cards' />
            )}
            <Box
              component='div'
              sx={{
                textAlign: 'center',
                mt: {
                  tablet: pxToRem(24),
                  mobile: '24px',
                },
              }}
            >
              <Button
                id='add-credit-card'
                variant='secondary'
                startIcon={<AddIcon />}
                sx={{
                  fontSize: '16px',
                  lineHeight: '150%',
                }}
                onClick={openAddCreditCardForm}
              >
                <FormattedMessage id='my_credit_cards.add_card' />
              </Button>
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>
    </Zoom>
  );
};
